<template>
  <div id="afdc-popup">
    <button id="afdc-popup-closer"><span class="afdc-sr-only">{{ $t('Close') }}</span><font-awesome-icon icon="times-circle" class="afdc-fa-icon"/></button>

    <div v-if="$store.getters.getLoadingStationDetails || !station_data || !station_data.id" id="afdc-popup-spinner-container">
      <div id="afdc-popup-spinner"></div>
    </div>

    <div id="afdc-popup-content" v-if="station_data && station_data.id">
      <h4><router-link v-bind:to="{ name: 'StationShow', params: { id: this.station_data.id } }">{{ station_data.station_name }}</router-link></h4>
      <div v-if="station_data.street_address" class="afdc-popup-address">
        {{ station_data.street_address }}<br>
        {{ stationCityStateZip(station_data.city, station_data.state, station_data.zip) }}
      </div>

      <div v-if="station_data.latitude && station_data.longitude && !$store.getters.isNavActiveTabCorridors" class="afdc-popup-directions afdc-kiosk-mode-hide">
        <a v-bind:href="stationDirections(station_data.latitude, station_data.longitude, station_data.street_address, stationCityStateZip(station_data.city, station_data.state, station_data.zip))" target="_blank" rel="noopener noreferrer"><font-awesome-icon icon="road" class="afdc-fa-icon"/>{{ $t("Directions") }}</a>
      </div>

      <div v-if="station_data.station_phone && !$store.getters.isNavActiveTabCorridors" class="afdc-clearfix afdc-popup-phone">
        <a v-for="phone in stationPhoneNumbers(station_data.station_phone)" v-bind:key="phone" v-bind:href="'tel:' + phone" itemprop="telephone" class="afdc-phone-link"><font-awesome-icon icon="phone" class="afdc-fa-icon"/>{{ phone }}</a>
      </div>

      <div v-if="station_data.fuel_type_code === 'ELEC' && !$store.getters.isNavActiveTabCorridors" class="afdc-popup-fuel-details">
        {{ stationEvLevels(station_data.ev_level1_evse_num, station_data.ev_level2_evse_num, station_data.ev_dc_fast_num) }}
      </div>

      <div v-if="station_data.fuel_type_code === 'ELEC' && $store.getters.isNavActiveTabCorridors" class="afdc-popup-fuel-details">
        <strong>{{ $t('Connectors') }}</strong><br />
        {{evConnectors}}
      </div>

      <div v-if = "!$store.getters.isNavActiveTabCorridors" class="afdc-float-left afdc-kiosk-mode-hide">
        <router-link v-bind:to="{ name: 'StationEdit', params: { id: this.station_data.id } }" class="afdc-station-edit-link">
          <small>{{ $t('Report a change') }}</small>
        </router-link>
      </div>

      <div class="afdc-clearfix afdc-popup-more" v-if = "!$store.getters.isNavActiveTabCorridors">
        <router-link v-bind:to="{ name: 'StationShow', params: { id: this.station_data.id } }"><font-awesome-icon icon="plus" class="afdc-fa-icon"/>{{ $t("More") }}</router-link>
      </div>

      <div class = "afdc-row" v-if="$store.getters.isNavActiveTabCorridors">
        <div class="afdc-col-auto">
          <button class="afdc-btn afdc-corridor-btn" :class="startActive" :disabled='isEnd' @click="setRouteStationStart()"><img src="@/assets/images/pins/start.png" class="afdc-corridor-corridor-pin" alt="route start pin" /> {{ $t('Start') }}</button>
        </div>
        <div class="afdc-col-auto">
          <button class="afdc-btn afdc-corridor-btn" :class="endActive" :disabled='isStart' @click="setRouteStationEnd()"><img src="@/assets/images/pins/end.png" class="afdc-corridor-corridor-pin"  alt="route end pin" /> {{ $t('End') }}</button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-unminified';

export default {
  name: 'MapStationPopup',
  props: ['map', 'feature', 'clickPoint'],

  data() {
    return {
      station_data: {},
    };
  },

  watch: {
    '$store.getters.getFilters': {
      handler: 'closePopup',
    },
  },

  computed: {
    isStart() {
      const corridorStart = this.$store.getters.getCorridorRouteStart.id || '';
      return this.station_data.id === corridorStart;
    },

    isEnd() {
      const corridorEnd = this.$store.getters.getCorridorRouteEnd.id || '';
      return this.station_data.id === corridorEnd;
    },

    startActive() {
      const corridorStart = this.$store.getters.getCorridorRouteStart.id || '';
      if (this.station_data.id === corridorStart) {
        return 'afdc-corridor-start-active';
      } return '';
    },

    endActive() {
      const corridorEnd = this.$store.getters.getCorridorRouteEnd.id || '';
      if (this.station_data.id === corridorEnd) {
        return 'afdc-corridor-end-active';
      } return '';
    },

    evConnectors() {
      const connectorTypes = this.station_data.ev_connector_types;
      const connectorArray = [];
      connectorTypes.forEach((connector) => connectorArray.push(this.stationEvConnectors(connector, 'label')));
      return connectorArray.join(' | ');
    },
  },

  beforeCreate() {
    this.popup = new mapboxgl.Popup({
      closeButton: false,
      maxWidth: 'none',
    });
  },

  mounted() {
    const coordinates = this.feature.geometry.coordinates.slice();

    while (Math.abs(this.clickPoint.lng - coordinates[0]) > 180) {
      coordinates[0] += this.clickPoint.lng > coordinates[0] ? 360 : -360;
    }

    this.popup.setLngLat(coordinates)
      .setDOMContent(this.$el)
      .addTo(this.map);

    this.$nextTick(() => {
      this.initPopup();
    });
  },

  beforeDestroy() {
    if (this.popup) {
      this.popup.remove();
    }
  },

  methods: {
    async initPopup() {
      // Trigger a map resize so that the map properly calculates the height of
      // the popup and adjusts its position to fit.
      this.map.resize();

      const closer = this.$el.querySelector('#afdc-popup-closer');
      closer.onclick = this.closePopup.bind(this);

      await this.$store.dispatch('fetchStationDetails', this.feature.properties.id);
      this.station_data = this.$store.getters.stationDetails;

      // After the content renders within the popup, trigger another resize
      // event so the popup will reposition if necessary to fit on the page
      // (since the height may be taller after content is added).
      this.$nextTick(() => {
        this.map.resize();
      });
    },

    closePopup() {
      this.$destroy();
    },

    setRouteStationStart() {
      const corridorStart = this.$store.getters.getCorridorRouteStart.id || '';
      if (this.station_data.id === corridorStart) {
        this.$store.commit('setCorridorRouteStart', {});
      } else {
        this.$store.commit('setCorridorRouteStart', this.station_data);
      }
    },
    setRouteStationEnd() {
      const corridorEnd = this.$store.getters.getCorridorRouteEnd.id || '';
      if (this.station_data.id === corridorEnd) {
        // toggle end point if it is already set to selected station
        this.$store.commit('setCorridorRouteEnd', {});
      } else {
        this.$store.commit('setCorridorRouteEnd', this.station_data);
      }
    },
  },
};
</script>

<style lang="scss">
@import '~spinthatshit/src/loaders.scss';

.mapboxgl-popup {
  z-index: 10;
}

.mapboxgl-popup-content {
  box-shadow: none;
  filter: drop-shadow(0px 1px 4px rgba(0, 0, 0, 0.2));
  border-radius: 10px;
}

#popup {
  width: 260px;
  height: auto;
  min-height: 120px;
}

#popup-spinner {
  @include loader02($size: 50px, $color: #666, $border-size: 10px);
  position: absolute;
  left: 50%;
  top: 35px;
  margin-left: -25px;
}

#popup-spinner-container {
  position: relative;
  margin: 0 auto;
  width: 50px;
  z-index: 10;
}

#popup-closer {
  float: right;
  color: #999;
  font-size: 25px;
  margin: 0px 0px 8px 8px;
}

#popup-closer:hover {
  cursor: pointer;
}

#popup-content {
  font-size: 16px;

  .popup-address {
    margin: 12px 0px;
  }

  .popup-directions, .popup-phone {
    margin: 3px 0px;
  }

  .popup-fuel-details {
    margin: 8px 0px;
  }

  .popup-more {
    margin-top: 8px;
    text-align: right;
  }

  .fa-icon {
    margin-right: 5px;
  }
}

.corridor-corridor-pin{
  height: 35px;
}

.corridor-btn {
  background: #e6e6e6;
  padding: 6px 14px;
  width: 90px;
  &:disabled {
    opacity: 0.55;
  }
  &:hover:enabled {
    background: #c3c3c3;
  }
}

.corridor-start-active {
  background: rgba(0, 128, 0, 0.2);
  &:hover:enabled {
    background: rgba(0, 128, 0, 0.3);
  }
}

.corridor-end-active {
  background: rgba(255, 0, 0, 0.2);
  &:hover:enabled {
    background: rgba(255, 0, 0, 0.3);
  }
}

</style>
